"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import NavBar from "../../home-page/components/NavBar";
import FooterSection from "../../home-page/components/FooterSection";
import { generateBreadcrumbSchema, generateOrganizationSchema } from '@/lib/utils/metadata';
import { testimonialService, Testimonial } from '@/lib/services/testimonialService';
import { landingPageService, LandingPageContent } from '@/lib/services/landingPageService';
import AppIcon from '@/components/ui/AppIcon';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

// Helper function to convert kebab-case icon names to PascalCase Heroicon names
const convertIconName = (iconName: string): string => {
  if (!iconName || typeof iconName !== 'string') return 'QuestionMarkCircleIcon';
  
  // Convert kebab-case to PascalCase and add 'Icon' suffix
  // e.g., 'clock' -> 'ClockIcon', 'map-pin' -> 'MapPinIcon'
  return iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('') + 'Icon';
};

export default function UsersLandingPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [heroStats, setHeroStats] = useState<any>(null);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [howItWorks, setHowItWorks] = useState<any[]>([]);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await testimonialService.getActive();
        setTestimonials(data.slice(0, 3)); // Show top 3 testimonials
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setContentLoading(true);
        setContentError(null);
        const content = await landingPageService.getByPageKey('users');
        
        // Map content by section key
        content.forEach((item: LandingPageContent) => {
          if (!item.isActive) return;
          
          switch (item.sectionKey) {
            case 'hero':
              setHeroStats(item.content);
              break;
            case 'benefits':
              setBenefits(item.content?.items || []);
              break;
            case 'howItWorks':
              setHowItWorks(item.content?.items || []);
              break;
          }
        });
      } catch (error) {
        console.error('Failed to load landing page content:', error);
        setContentError('Failed to load content');
        // Set fallback content
        setFallbackContent();
      } finally {
        setContentLoading(false);
      }
    };

    loadContent();
  }, []);

  const setFallbackContent = () => {
    setHeroStats({
      deliveries: "10K+",
      rating: "4.9/5",
      cities: "5 Cities"
    });

    setBenefits([
      {
        icon: (
          <svg width="28" height="28" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        ),
        title: "On-Demand Service",
        description: "Book delivery exactly when you need it. No waiting, no scheduling weeks in advance."
      },
      {
        icon: (
          <svg width="28" height="28" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
        ),
        title: "Real-Time Tracking",
        description: "Track your driver and delivery in real-time. Know exactly when your items will arrive."
      }
    ]);

    setHowItWorks([
      {
        step: "1",
        title: "Create Your Account",
        description: "Sign up in minutes with your basic information. No credit card required to start."
      },
      {
        step: "2",
        title: "Request a Delivery",
        description: "Tell us what you need moved, where it's going, and when. Get an instant quote."
      }
    ]);
  };

  return (
    <>
      {/* Structured Data - WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Be a User - Reliable On-Demand Delivery Services',
            description: 'Experience hassle-free delivery for furniture, appliances, and large items across Ontario.',
            url: `${SITE_URL}/landing/users`,
            breadcrumb: generateBreadcrumbSchema([
              { name: 'Home', url: `${SITE_URL}/home-page` },
              { name: 'Landing', url: `${SITE_URL}/landing` },
              { name: 'Users', url: `${SITE_URL}/landing/users` }
            ])
          })
        }}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateOrganizationSchema())
        }}
      />

      <NavBar />

      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden py-20 md:py-32">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-brand-purple/5 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-green/5 translate-y-1/4 -translate-x-1/4 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-purple/20 shadow-purple-sm">
              <span className="w-2 h-2 rounded-full bg-brand-purple animate-pulse" />
              <span className="text-sm font-semibold text-dark-navy">For Users</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-deep-navy">
              Delivery Made <span className="text-brand-purple">Simple</span>
            </h1>

            <p className="text-lg md:text-xl text-body-text leading-relaxed">
              {contentLoading ? (
                <span className="inline-block w-64 h-6 bg-gray-200 animate-pulse rounded" />
              ) : (
                "Get furniture, appliances, and large items delivered on-demand across Ontario. Real-time tracking, professional drivers, and hassle-free service."
              )}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <Link href="/be-a-user" className="btn-primary">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Sign Up as User
              </Link>
              <Link href="/contact-us" className="btn-secondary">
                Contact Us
              </Link>
            </div>

            {contentLoading ? (
              <div className="flex items-center justify-center gap-8 pt-6">
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
                <div className="w-px h-10 bg-border-gray" />
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
                <div className="w-px h-10 bg-border-gray" />
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
              </div>
            ) : (
              <div className="flex items-center justify-center gap-8 pt-6">
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-purple">{heroStats?.deliveries || '10K+'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Deliveries</p>
                </div>
                <div className="w-px h-10 bg-border-gray" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-green">{heroStats?.rating || '4.9/5'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Rating</p>
                </div>
                <div className="w-px h-10 bg-border-gray" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-purple">{heroStats?.cities || '5 Cities'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Coverage</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Why Choose <span className="text-brand-purple">GoGedit</span>?
            </h2>
            <p className="text-lg text-body-text">
              We fill the gap where other services fail on size, quality, and attention to detail.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-48" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border-gray/50"
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-purple/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    {typeof benefit.icon === 'string' ? (
                      <AppIcon
                        name={convertIconName(benefit.icon)}
                        size={28}
                        className="text-brand-purple"
                      />
                    ) : (
                      benefit.icon
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-deep-navy mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-body-text leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-light-purple/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              How It <span className="text-brand-purple">Works</span>
            </h2>
            <p className="text-lg text-body-text">
              Get started in four simple steps. It's that easy.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-56" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-card h-full">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple to-accent-purple flex items-center justify-center text-white font-heading font-bold text-xl mb-4">
                      {item.step}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-deep-navy mb-2">
                      {item.title}
                    </h3>
                    <p className="text-body-text text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-brand-purple to-transparent" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {!loading && testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
                What Our <span className="text-brand-purple">Users</span> Say
              </h2>
              <p className="text-lg text-body-text">
                Join thousands of satisfied customers across Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 border border-border-gray/50"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        width="16"
                        height="16"
                        fill={i < testimonial.rating ? "#F59E0B" : "#E5E7EB"}
                        viewBox="0 0 24 24"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-body-text leading-relaxed mb-4">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-purple/10 flex items-center justify-center text-brand-purple font-heading font-bold">
                      {testimonial.customerName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-deep-navy text-sm">
                        {testimonial.customerName}
                      </p>
                      {testimonial.customerRole && (
                        <p className="text-muted-text text-xs">
                          {testimonial.customerRole}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple to-accent-purple relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-white blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Hassle-Free Delivery?
          </h2>
          <p className="text-white/90 text-lg mb-8 leading-relaxed">
            Join thousands of users who trust GoGedit for their moving and delivery needs.
            Sign up today and get your first delivery quote instantly.
          </p>
          <Link href="/be-a-user" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-purple rounded-full font-heading font-bold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Sign Up Now - It's Free
          </Link>
        </div>
      </section>

      {/* Cross-links to other landing pages */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-deep-navy text-center mb-8">
            Explore More with GoGedit
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link href="/landing/drivers">
              <div className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-brand-green/20 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <svg width="22" height="22" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
                    <rect x="1" y="3" width="15" height="13" rx="2" />
                    <path d="M16 8h4l3 5v3h-7V8z" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-dark-navy group-hover:text-brand-green transition-colors">Want to Earn?</h3>
                  <p className="text-sm text-body-text mt-1">Become a GoGedit driver today</p>
                </div>
                <svg width="18" height="18" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </Link>
            <Link href="/landing/retailers">
              <div className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-brand-green/20 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <svg width="22" height="22" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-dark-navy group-hover:text-brand-green transition-colors">Are You a Retailer?</h3>
                  <p className="text-sm text-body-text mt-1">Partner with us to grow your business</p>
                </div>
                <svg width="18" height="18" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />
    </>
  );
}